import type { ReactNode } from "react";
import type { TopicGlyph } from "../scenario/topics";

/**
 * Line icons for the intro card: one per course topic (`COURSE_TOPICS[..].glyph`
 * in scenario/topics.ts) plus the course facts row (duration, quiz, what the
 * learner needs). Same single-colour stroke style as `TimelineIcon`.
 */
export type IntroGlyphKind = TopicGlyph | "clock" | "quiz" | "sound" | "mic" | "keyboard";

const GLYPHS: Record<IntroGlyphKind, ReactNode> = {
  mail: (
    <>
      <rect x="3" y="5.5" width="18" height="13" rx="1.8" />
      <path d="M3.5 6.5 12 13l8.5-6.5" />
    </>
  ),
  target: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="4.5" />
      <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
    </>
  ),
  qr: (
    <>
      <rect x="4" y="4" width="6" height="6" rx="1" />
      <rect x="14" y="4" width="6" height="6" rx="1" />
      <rect x="4" y="14" width="6" height="6" rx="1" />
      <path d="M14 14h2.5v2.5H14zM18 18h2v2h-2zM14 19.5h1.5M19.5 14v1.5" />
    </>
  ),
  message: (
    <path d="M4 5.5h16a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H9l-4.5 3.5V16.5H4a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1Z" />
  ),
  phone: (
    <path d="M6.6 3.5 9.4 9 6.8 11.4c1.1 2.2 3.2 4.3 5.4 5.4l2.4-2.4 5.5 2.8v3.1c0 1-.9 1.7-1.9 1.5C10.8 20.4 4.5 14.1 3.5 6.4c-.1-1 .6-1.9 1.6-1.9h1.5Z" />
  ),
  video: (
    <>
      <rect x="3" y="6" width="13" height="12" rx="2" />
      <path d="m16 10.5 5-3v9l-5-3" />
    </>
  ),
  money: (
    <>
      <rect x="2.5" y="6" width="19" height="12" rx="1.8" />
      <circle cx="12" cy="12" r="2.8" />
      <path d="M6 9.5v5M18 9.5v5" />
    </>
  ),
  key: (
    <>
      <circle cx="8" cy="15" r="4" />
      <path d="m11 12 8.5-8.5M16.5 6.5 19 9M14.5 8.5l2 2" />
    </>
  ),
  badge: (
    <>
      <rect x="5.5" y="3.5" width="13" height="17" rx="2.2" />
      <circle cx="12" cy="10" r="2.6" />
      <path d="M8.8 16.5c.6-1.7 2-2.6 3.2-2.6s2.6.9 3.2 2.6" />
    </>
  ),
  door: (
    <>
      <path d="M6 20.5V4.5a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v16" />
      <path d="M3.5 20.5h17M14.5 12.5h.01" />
    </>
  ),
  usb: (
    <>
      <rect x="9.3" y="2.5" width="5.4" height="7" rx="1" />
      <path d="M12 9.5v5.5M8.5 15h7v3.5a2 2 0 0 1-2 2h-3a2 2 0 0 1-2-2Z" />
    </>
  ),
  bug: (
    <>
      <rect x="7.5" y="8" width="9" height="12" rx="4.5" />
      <path d="M9.5 8.5a2.5 2.5 0 0 1 5 0M3.5 13h4M16.5 13h4M4.5 8.5l3 2M19.5 8.5l-3 2M4.5 18.5l3-2M19.5 18.5l-3-2" />
    </>
  ),
  alert: (
    <>
      <path d="M12 3.6 21.2 19.8H2.8Z" />
      <path d="M12 10v4.2" />
      <circle cx="12" cy="17.2" r=".9" fill="currentColor" stroke="none" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.6 2.6 4 5.8 4 9s-1.4 6.4-4 9c-2.6-2.6-4-5.8-4-9s1.4-6.4 4-9Z" />
    </>
  ),
  lock: (
    <>
      <rect x="5" y="10.5" width="14" height="10" rx="2" />
      <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3M12 14.5v2.5" />
    </>
  ),
  database: (
    <>
      <ellipse cx="12" cy="6" rx="7.5" ry="2.8" />
      <path d="M4.5 6v12c0 1.5 3.4 2.8 7.5 2.8s7.5-1.3 7.5-2.8V6M4.5 12c0 1.5 3.4 2.8 7.5 2.8s7.5-1.3 7.5-2.8" />
    </>
  ),
  eye: (
    <>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  wifi: (
    <>
      <path d="M2.5 9a14 14 0 0 1 19 0M5.5 12.5a9.5 9.5 0 0 1 13 0M8.5 16a5 5 0 0 1 7 0" />
      <circle cx="12" cy="19" r=".9" fill="currentColor" stroke="none" />
    </>
  ),
  laptop: (
    <>
      <rect x="4.5" y="5" width="15" height="10" rx="1.2" />
      <path d="M2.5 19h19l-1.5-4h-16Z" />
    </>
  ),
  mobile: (
    <>
      <rect x="7" y="2.5" width="10" height="19" rx="2" />
      <path d="M11 18.5h2" />
    </>
  ),
  folder: (
    <path d="M3.5 6.5A1.5 1.5 0 0 1 5 5h4.4l1.8 2.2H19a1.5 1.5 0 0 1 1.5 1.5v8.8A1.5 1.5 0 0 1 19 19H5a1.5 1.5 0 0 1-1.5-1.5Z" />
  ),
  share: (
    <>
      <circle cx="6" cy="12" r="2.5" />
      <circle cx="18" cy="6" r="2.5" />
      <circle cx="18" cy="18" r="2.5" />
      <path d="m8.2 10.8 7.6-3.6M8.2 13.2l7.6 3.6" />
    </>
  ),
  chat: (
    <>
      <path d="M4 5.5h16a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H9l-4.5 3.5V16.5H4a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1Z" />
      <path d="M8 11h.01M12 11h.01M16 11h.01" strokeWidth="2.4" />
    </>
  ),
  check: (
    <>
      <path d="M12 3.2 19.5 6v6c0 4.6-3.2 8-7.5 9-4.3-1-7.5-4.4-7.5-9V6Z" />
      <path d="m8.8 12 2.3 2.3 4.3-4.6" />
    </>
  ),
  bell: (
    <>
      <path d="M6 16.5V11a6 6 0 0 1 12 0v5.5l1.5 2h-15Z" />
      <path d="M10 20.5a2 2 0 0 0 4 0" />
    </>
  ),
  book: (
    <>
      <path d="M4 5a2 2 0 0 1 2-2h13v15H6a2 2 0 0 0-2 2Z" />
      <path d="M4 20a2 2 0 0 0 2 1.5h13V18M8.5 7.5h6" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5.3l3.6 2.1" />
    </>
  ),
  quiz: (
    <>
      <rect x="5" y="3.5" width="14" height="17" rx="2" />
      <path d="m8.5 9 1.5 1.5 2.5-3M8.5 15.5l1.5 1.5 2.5-3M14.5 9.5h1.5M14.5 16h1.5" />
    </>
  ),
  sound: (
    <>
      <path d="M4 14v-4a1 1 0 0 1 1-1h3l4.5-4v14L8 15H5a1 1 0 0 1-1-1Z" />
      <path d="M16 9a4 4 0 0 1 0 6M18.5 6.5a7.5 7.5 0 0 1 0 11" />
    </>
  ),
  mic: (
    <>
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5v3.5" />
    </>
  ),
  keyboard: (
    <>
      <rect x="2.5" y="6" width="19" height="12" rx="1.8" />
      <path d="M6 10h.01M9 10h.01M12 10h.01M15 10h.01M18 10h.01M7.5 14h9" strokeWidth="2" />
    </>
  ),
};

export function IntroGlyph({ kind, className }: { kind: IntroGlyphKind; className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {GLYPHS[kind]}
    </svg>
  );
}
